import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getApiUrl } from '../api';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../context/LanguageContext';
import styles from './CustomerDashboard.module.css';

// ── Safe fetch ───────────────────────────────────────────────────────────────
const safeApiFetch = async (url, options = {}) => {
  const res = await fetch(url, options);
  const text = await res.text();

  // Detect HTML error pages (e.g. Express 404 "Cannot GET /api/...")
  const isHtml = text.trimStart().startsWith('<');
  if (isHtml) {
    const plain = text.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
    throw new Error(
      plain.length < 120 ? plain : `Server error (${res.status}) — make sure the backend has been restarted.`
    );
  }

  let data;
  try { data = JSON.parse(text); } catch { data = { message: text }; }
  if (!res.ok) throw new Error(data?.message || `Error ${res.status}`);
  return data;
};

// ── Icons ────────────────────────────────────────────────────────────────────
const Icon = ({ children, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round"
    strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const IconSettings = () => (
  <Icon>
    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
    <circle cx="12" cy="12" r="3" />
  </Icon>
);

const IconStats = () => (
  <Icon>
    <path d="M3 3v18h18" />
    <path d="M18 17V9" />
    <path d="M13 17V5" />
    <path d="M8 17v-3" />
  </Icon>
);

const IconBilling = () => (
  <Icon>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <line x1="2" y1="10" x2="22" y2="10" />
  </Icon>
);

const IconBack = () => (
  <Icon size={14}>
    <path d="M19 12H5" />
    <path d="m12 5-7 7 7 7" />
  </Icon>
);

const IconSave = () => (
  <Icon size={16}>
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </Icon>
);

const IconMapPin = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const IconPhone = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.61 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.55a16 16 0 0 0 6.29 6.29l.61-.61a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const IconSearch = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const IconCheck = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconClose = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const IconBuilding = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect width="16" height="20" x="4" y="2" rx="2" ry="2" />
    <path d="M9 22v-4h6v4" />
    <path d="M8 6h.01M16 6h.01M12 6h.01M12 10h.01M8 10h.01M16 10h.01M12 14h.01M8 14h.01M16 14h.01" />
  </svg>
);

// ── TONE / LANGUAGE OPTIONS ──────────────────────────────────────────────────
const TONE_OPTIONS = [
  { value: 'professional', label: 'Professional', desc: 'Formal and business-like responses' },
  { value: 'friendly',     label: 'Friendly',     desc: 'Warm, approachable, and conversational' },
  { value: 'empathetic',   label: 'Empathetic',   desc: 'Compassionate and understanding tone' },
  { value: 'concise',      label: 'Concise',      desc: 'Short, direct, and to the point' },
  { value: 'enthusiastic', label: 'Enthusiastic', desc: 'Energetic and positive responses' },
];

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'ar', label: 'Arabic' },
];

// ── Google Logo ──────────────────────────────────────────────────────────────
const GoogleLogo = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ marginRight: 8 }}>
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

// ═══════════════════════════════════════════════════════════════════════════
// LOCATION WIZARD MODAL
// ═══════════════════════════════════════════════════════════════════════════

const WIZARD_STEPS = [
  { id: 'fetch',   label: 'Fetch Locations' },
  { id: 'select',  label: 'Select Locations' },
  { id: 'confirm', label: 'Confirm' },
];

const LocationWizardModal = ({ token, onClose, onSaved, initialSelected = [] }) => {
  // step: 'fetch' | 'select' | 'confirm'
  const [step, setStep] = useState('fetch');
  const [gbpLocations, setGbpLocations] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(() =>
    new Set(initialSelected.map(l => l.locationId))
  );
  // Map of locationId → full location object (for confirm step)
  const [locationMap, setLocationMap] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Fetch available GBP locations on mount
  useEffect(() => {
    setStep('fetch');
    setFetchError(null);
    safeApiFetch(getApiUrl('/api/auth/google/gbp-locations'), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(d => {
        const locs = d.gbpLocations || [];
        setGbpLocations(locs);
        // Build lookup map
        const map = {};
        locs.forEach(l => { map[l.locationId] = l; });
        setLocationMap(map);
        setStep('select');
      })
      .catch(err => {
        setFetchError(err.message);
        setStep('select'); // still advance so user sees the error
      });
  }, [token]);

  const stepIndex = WIZARD_STEPS.findIndex(s => s.id === step);

  const filteredLocations = gbpLocations.filter(l =>
    !search ||
    l.locationName.toLowerCase().includes(search.toLowerCase()) ||
    l.address.toLowerCase().includes(search.toLowerCase())
  );

  const allVisible = filteredLocations.length > 0 &&
    filteredLocations.every(l => selected.has(l.locationId));

  const toggleLocation = (locId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(locId)) { next.delete(locId); } else { next.add(locId); }
      return next;
    });
  };

  const toggleAll = () => {
    if (allVisible) {
      setSelected(prev => {
        const next = new Set(prev);
        filteredLocations.forEach(l => next.delete(l.locationId));
        return next;
      });
    } else {
      setSelected(prev => {
        const next = new Set(prev);
        filteredLocations.forEach(l => next.add(l.locationId));
        return next;
      });
    }
  };

  const handleConfirm = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const selectedLocations = Array.from(selected).map(id => ({
        locationId:   id,
        locationName: locationMap[id]?.locationName || '',
        address:      locationMap[id]?.address || '',
      }));
      await safeApiFetch(getApiUrl('/api/auth/google/locations/selection'), {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ selectedLocations }),
      });
      onSaved(selectedLocations);
      onClose();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const selectedCount = selected.size;

  // Close on backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className={styles.wizardOverlay} onClick={handleBackdropClick} role="dialog" aria-modal="true" aria-label="Select GBP Locations">
      <div className={styles.wizardCard}>
        {/* ── Header ───────────────────────────────────────────── */}
        <div className={styles.wizardHeader}>
          <div className={styles.wizardTitleRow}>
            <div>
              <h2 className={styles.wizardTitle}>Select Business Locations</h2>
              <p className={styles.wizardSubtitle}>
                Choose which Google Business Profile locations to track for review processing.
              </p>
            </div>
            <button className={styles.wizardCloseBtn} onClick={onClose} aria-label="Close wizard">
              <IconClose size={16} />
            </button>
          </div>

          {/* Step progress */}
          <div className={styles.wizardSteps}>
            {WIZARD_STEPS.map((s, i) => {
              const isDone   = i < stepIndex;
              const isActive = i === stepIndex;
              return (
                <React.Fragment key={s.id}>
                  <div className={styles.wizardStep}>
                    <div className={`${styles.wizardStepDot} ${isActive ? styles.dotActive : ''} ${isDone ? styles.dotDone : ''}`}>
                      {isDone ? <IconCheck size={11} /> : i + 1}
                    </div>
                    <span className={`${styles.wizardStepLabel} ${isActive ? styles.labelActive : ''} ${isDone ? styles.labelDone : ''}`}>
                      {s.label}
                    </span>
                  </div>
                  {i < WIZARD_STEPS.length - 1 && (
                    <div className={`${styles.wizardStepConnector} ${isDone ? styles.connectorDone : ''}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
          <div className={styles.wizardDivider} />
        </div>

        {/* ── Body ─────────────────────────────────────────────── */}
        <div className={styles.wizardBody}>

          {/* STEP: fetch / loading */}
          {step === 'fetch' && (
            <div className={styles.wizardLoadingWrap}>
              <div className={styles.wizardLoadingSpinner} />
              <p className={styles.wizardLoadingText}>Fetching your Google Business Profile locations…</p>
            </div>
          )}

          {/* STEP: select */}
          {step === 'select' && (
            <>
              {fetchError && (
                <div className={styles.alertError} style={{ marginBottom: 16 }}>{fetchError}</div>
              )}
              {saveError && (
                <div className={styles.alertError} style={{ marginBottom: 16 }}>{saveError}</div>
              )}

              {gbpLocations.length === 0 && !fetchError ? (
                <div className={styles.wizardEmptyState}>
                  <IconMapPin size={40} />
                  <p>No locations found</p>
                  <span>
                    No Business Profile locations were found for your Google account.
                    Make sure your GBP account is set up correctly.
                  </span>
                </div>
              ) : (
                <>
                  {/* Search */}
                  <div className={styles.wizardSearch}>
                    <span className={styles.wizardSearchIcon}><IconSearch size={15} /></span>
                    <input
                      type="text"
                      className={styles.wizardSearchInput}
                      placeholder="Search locations by name or address…"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                  </div>

                  {/* Select-all bar */}
                  <div className={styles.wizardSelectBar}>
                    <span className={styles.wizardSelectCount}>
                      <strong>{selectedCount}</strong> of {gbpLocations.length} selected
                    </span>
                    <button className={styles.wizardSelectAllBtn} onClick={toggleAll}>
                      {allVisible ? 'Deselect Visible' : 'Select All Visible'}
                    </button>
                  </div>

                  {/* Location cards */}
                  {filteredLocations.length === 0 ? (
                    <div className={styles.wizardEmptyState}>
                      <IconSearch size={36} />
                      <p>No results</p>
                      <span>Try a different search term.</span>
                    </div>
                  ) : (
                    <div className={styles.locationGrid}>
                      {filteredLocations.map(loc => {
                        const isSelected = selected.has(loc.locationId);
                        return (
                          <button
                            key={loc.locationId}
                            type="button"
                            className={`${styles.locationCard} ${isSelected ? styles.locationSelected : ''}`}
                            onClick={() => toggleLocation(loc.locationId)}
                            aria-pressed={isSelected}
                          >
                            {/* Check circle */}
                            <div className={styles.locationCardCheck}>
                              {isSelected && <IconCheck size={11} />}
                            </div>

                            {/* Building icon */}
                            <div className={styles.locationCardIcon}>
                              <IconBuilding size={18} />
                            </div>

                            {/* Info */}
                            <div className={styles.locationCardName}>{loc.locationName || 'Unnamed Location'}</div>
                            {loc.address && (
                              <div className={styles.locationCardAddress}>{loc.address}</div>
                            )}
                            <div className={styles.locationCardMeta}>
                              {loc.primaryPhone && (
                                <span className={styles.locationCardMetaItem}>
                                  <IconPhone size={11} />
                                  {loc.primaryPhone}
                                </span>
                              )}
                              {loc.websiteUri && (
                                <span className={styles.locationCardMetaItem}>
                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>
                                  Website
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* STEP: confirm */}
          {step === 'confirm' && (
            <div className={styles.wizardConfirmWrap}>
              <div className={styles.wizardConfirmIcon}>
                <IconMapPin size={32} />
              </div>
              <h3 className={styles.wizardConfirmTitle}>
                {selectedCount === 1 ? '1 location selected' : `${selectedCount} locations selected`}
              </h3>
              <p className={styles.wizardConfirmDesc}>
                Reviews from the following locations will be synced and processed. You can change this selection anytime from Settings.
              </p>
              <div className={styles.wizardConfirmList}>
                {Array.from(selected).map(id => {
                  const loc = locationMap[id];
                  return (
                    <div key={id} className={styles.wizardConfirmItem}>
                      <div className={styles.wizardConfirmItemDot} />
                      <span>{loc?.locationName || id}</span>
                    </div>
                  );
                })}
              </div>
              {saveError && (
                <div className={styles.alertError} style={{ width: '100%', maxWidth: 420 }}>{saveError}</div>
              )}
            </div>
          )}
        </div>

        {/* ── Footer ───────────────────────────────────────────── */}
        <div className={styles.wizardFooter}>
          <div className={styles.wizardFooterLeft}>
            {step === 'confirm' && (
              <button className={styles.wizardBtnSecondary} onClick={() => setStep('select')} disabled={saving}>
                ← Back
              </button>
            )}
          </div>
          <div className={styles.wizardFooterRight}>
            <button className={styles.wizardBtnSecondary} onClick={onClose}>
              Cancel
            </button>
            {step === 'select' && (
              <button
                className={styles.wizardBtnPrimary}
                onClick={() => setStep('confirm')}
                disabled={selectedCount === 0}
              >
                Continue →
              </button>
            )}
            {step === 'confirm' && (
              <button
                className={styles.wizardBtnPrimary}
                onClick={handleConfirm}
                disabled={saving}
              >
                {saving ? (
                  <span className={styles.wizardBtnSaving}>
                    <div className={styles.spinnerSm} /> Saving…
                  </span>
                ) : (
                  '✓ Confirm & View Stats'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// PANEL — Settings
// ═══════════════════════════════════════════════════════════════════════════
const SettingsPanel = ({ token, selectedLocations, onOpenWizard }) => {
  const [settings, setSettings] = useState({ tone: 'professional', language: 'en', signature: '' });
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [error, setError]       = useState(null);

  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    safeApiFetch(getApiUrl('/api/user/settings'), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(d => setSettings({ tone: 'professional', language: 'en', signature: '', ...d.settings }))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));

    setLoadingLocations(true);
    safeApiFetch(getApiUrl('/api/auth/google/locations'), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(d => setLocations(d.locations || []))
      .catch(e => console.error('Failed to fetch locations in settings:', e.message))
      .finally(() => setLoadingLocations(false));
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const data = await safeApiFetch(getApiUrl('/api/user/settings'), {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });
      setSettings(prev => ({ ...prev, ...data.settings }));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async (locationId) => {
    if (!window.confirm('Are you sure you want to disconnect this Google account?')) return;
    setDisconnecting(true);
    setError(null);
    try {
      await safeApiFetch(getApiUrl(`/api/auth/google/disconnect/${locationId}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setLocations(prev => prev.filter(l => l.locationId !== locationId));
    } catch (err) {
      setError(err.message || 'Failed to disconnect account.');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    setError(null);
    try {
      const res = await fetch(getApiUrl('/api/auth/google/url'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `Server error (${res.status})`);
      }
      const { url } = await res.json();
      window.location.href = url;
    } catch (err) {
      setError(err.message || 'Failed to initiate Google connection.');
      setConnecting(false);
    }
  };

  if (loading) return <div className={styles.loadingWrap}><div className={styles.spinner} /></div>;

  const isGbpConnected = locations.length > 0;

  return (
    <div className={styles.settingsWrap}>
      {error && <div className={styles.alertError}>{error}</div>}

      {/* Tone */}
      <div className={styles.settingsSection}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Response Tone</h2>
          <p className={styles.sectionDesc}>Choose how your AI-crafted replies will sound to customers.</p>
        </div>
        <div className={styles.toneGrid}>
          {TONE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              className={`${styles.toneCard} ${settings.tone === opt.value ? styles.toneActive : ''}`}
              onClick={() => setSettings(s => ({ ...s, tone: opt.value }))}
            >
              <div className={styles.toneLabel}>{opt.label}</div>
              <div className={styles.toneDesc}>{opt.desc}</div>
              {settings.tone === opt.value && (
                <div className={styles.toneCheck}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.divider} />

      {/* Language */}
      <div className={styles.settingsSection}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Reply Language</h2>
          <p className={styles.sectionDesc}>The language your replies will be written in.</p>
        </div>
        <select
          className={styles.select}
          value={settings.language}
          onChange={e => setSettings(s => ({ ...s, language: e.target.value }))}
        >
          {LANGUAGE_OPTIONS.map(l => (
            <option key={l.value} value={l.value}>{l.label}</option>
          ))}
        </select>
      </div>

      <div className={styles.divider} />

      {/* Signature */}
      <div className={styles.settingsSection}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Custom Signature</h2>
          <p className={styles.sectionDesc}>Optional closing line appended to every reply (e.g. "— The Team at Acme Co.").</p>
        </div>
        <input
          type="text"
          className={styles.textInput}
          placeholder='e.g. — The Team at Acme Co.'
          value={settings.signature}
          onChange={e => setSettings(s => ({ ...s, signature: e.target.value }))}
          maxLength={120}
        />
        <div className={styles.charCount}>{settings.signature.length}/120</div>
      </div>

      <div className={styles.divider} />

      {/* Connected Accounts */}
      <div className={styles.settingsSection}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Connected Accounts</h2>
          <p className={styles.sectionDesc}>Manage your integration with Google Business Profile.</p>
        </div>
        {loadingLocations ? (
          <div className={styles.spinner} style={{ width: 20, height: 20, borderWidth: 2 }} />
        ) : isGbpConnected ? (
          <>
            <div className={styles.connectedAccountCard}>
              <div className={styles.connectedAccountInfo}>
                <GoogleLogo size={20} />
                <div>
                  <div className={styles.connectedAccountName}>
                    {locations[0].name || locations[0].locationName || 'Google Business Profile'}
                  </div>
                  <div className={styles.connectedAccountDetails}>
                    ID: {locations[0].googleAccountId || locations[0].locationId}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className={styles.disconnectBtn}
                onClick={() => handleDisconnect(locations[0].locationId)}
                disabled={disconnecting}
              >
                {disconnecting ? 'Disconnecting...' : 'Disconnect'}
              </button>
            </div>

            {/* ── Manage Tracked Locations ── */}
            <div className={styles.manageLocationsRow}>
              <div className={styles.manageLocationsInfo}>
                <div className={styles.manageLocationsTitle}>Tracked Locations</div>
                <div className={styles.manageLocationsCount}>
                  {selectedLocations.length === 0
                    ? 'No locations selected yet — reviews won\'t be synced until you choose.'
                    : `${selectedLocations.length} location${selectedLocations.length !== 1 ? 's' : ''} selected for review sync`}
                </div>
              </div>
              <button
                type="button"
                className={styles.manageLocationsBtn}
                onClick={onOpenWizard}
              >
                <IconMapPin size={14} />
                {selectedLocations.length === 0 ? 'Select Locations' : 'Edit Locations'}
              </button>
            </div>
          </>
        ) : (
          <div className={styles.unconnectedAccountCard}>
            <p className={styles.unconnectedText}>No Google account connected.</p>
            <button
              type="button"
              className={styles.connectLinkBtn}
              onClick={handleConnect}
              disabled={connecting}
            >
              {connecting ? 'Connecting...' : 'Connect Google Account'}
            </button>
          </div>
        )}
      </div>

      <div className={styles.divider} />

      <div className={styles.settingsActions}>
        <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
          {saving ? <div className={styles.spinnerSm} /> : <IconSave />}
          {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Changes'}
        </button>
        {saved && <span className={styles.savedMsg}>Your preferences have been updated.</span>}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// PANEL — My Stats
// ═══════════════════════════════════════════════════════════════════════════

const SortIcon = ({ active, direction }) => {
  if (!active) {
    return (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 6, opacity: 0.4 }}>
        <path d="m7 15 5 5 5-5M7 9l5-5 5 5"/>
      </svg>
    );
  }
  return direction === 'asc' ? (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 6, color: '#9219a6' }}>
      <path d="m18 15-6-6-6 6"/>
    </svg>
  ) : (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 6, color: '#9219a6' }}>
      <path d="m6 9 6 6 6-6"/>
    </svg>
  );
};

const StatsPanel = ({ token, selectedLocations, onOpenWizard }) => {
  const [stats, setStats]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError]           = useState(null);
  const [gbpConnected, setGbpConnected] = useState(false);
  // 'all' means aggregate across all selected locations
  const [activeLocationId, setActiveLocationId] = useState('all');

  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('desc');

  // Initial load: check GBP connection
  useEffect(() => {
    if (!token) return;
    setLoading(true);
    safeApiFetch(getApiUrl('/api/auth/google/locations'), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(d => {
        const locs = d.locations || [];
        setGbpConnected(locs.length > 0);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  // Fetch stats whenever the active location filter or selectedLocations changes
  useEffect(() => {
    if (!token || !gbpConnected || selectedLocations.length === 0) return;
    setStatsLoading(true);
    const qs = activeLocationId !== 'all'
      ? `?locationId=${encodeURIComponent(activeLocationId)}`
      : '';
    safeApiFetch(getApiUrl(`/api/user/stats${qs}`), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(d => setStats(d))
      .catch(e => setError(e.message))
      .finally(() => setStatsLoading(false));
  }, [token, gbpConnected, selectedLocations, activeLocationId]);

  const handleConnect = async () => {
    setConnecting(true);
    setError(null);
    try {
      const res = await fetch(getApiUrl('/api/auth/google/url'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `Server error (${res.status})`);
      }
      const { url } = await res.json();
      window.location.href = url;
    } catch (err) {
      setError(err.message || 'Failed to initiate Google connection.');
      setConnecting(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getStarColor = (rating) => {
    if (rating >= 4) return '#fbbf24'; // Golden Yellow
    if (rating === 3) return '#f97316'; // Orange
    return '#ef4444'; // Red
  };

  // Derive sorted reviews from stats data — must be declared before any early returns
  const rawReviews = stats?.reviews || [];
  const sortedReviews = React.useMemo(() => {
    if (!sortField) return rawReviews;
    return [...rawReviews].sort((a, b) => {
      let valA, valB;
      if (sortField === 'rating') {
        valA = a.rating || 0;
        valB = b.rating || 0;
      } else if (sortField === 'review') {
        valA = (a.reviewText || '').trim().toLowerCase();
        valB = (b.reviewText || '').trim().toLowerCase();
      } else if (sortField === 'date') {
        valA = a.reviewDate ? new Date(a.reviewDate).getTime() : 0;
        valB = b.reviewDate ? new Date(b.reviewDate).getTime() : 0;
      } else if (sortField === 'status') {
        // replied = 1, pending = 0  so descending puts replied first
        valA = a.replied ? 1 : 0;
        valB = b.replied ? 1 : 0;
      } else {
        return 0;
      }
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rawReviews, sortField, sortDirection]);


  if (loading) return <div className={styles.loadingWrap}><div className={styles.spinner} /></div>;
  if (error)   return <div className={styles.alertError}>{error}</div>;

  // ── Case 1: GBP not connected ─────────────────────────────────────────
  if (!gbpConnected) {
    return (
      <div className={styles.connectContainer}>
        <div className={styles.connectCard}>
          <div className={styles.connectIconWrap}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9219a6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <h2 className={styles.connectTitle}>Connect Google Business Profile</h2>
          <p className={styles.connectDesc}>
            To view your business statistics and manage reviews, please connect your Google Business Profile account.
          </p>

          <div className={styles.connectBenefits}>
            <div className={styles.benefitItem}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={styles.benefitCheck}><polyline points="20 6 9 17 4 12" /></svg>
              <span>Sync and fetch customer reviews automatically</span>
            </div>
            <div className={styles.benefitItem}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={styles.benefitCheck}><polyline points="20 6 9 17 4 12" /></svg>
              <span>AI-crafted draft replies matching your brand tone</span>
            </div>
            <div className={styles.benefitItem}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={styles.benefitCheck}><polyline points="20 6 9 17 4 12" /></svg>
              <span>Post replies directly back to Google in one click</span>
            </div>
          </div>

          <button
            type="button"
            className={styles.connectBtn}
            onClick={handleConnect}
            disabled={connecting}
          >
            {connecting ? (
              <>
                <div className={styles.spinnerSm} style={{ marginRight: 8 }} />
                Redirecting to Google...
              </>
            ) : (
              <>
                <GoogleLogo />
                Connect Google Account
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // ── Case 2: GBP connected but no locations selected yet ──────────────
  if (selectedLocations.length === 0) {
    return (
      <div className={styles.connectContainer}>
        <div className={styles.connectCard}>
          <div className={styles.connectIconWrap}>
            <IconMapPin size={40} />
          </div>
          <h2 className={styles.connectTitle}>Select Your Locations</h2>
          <p className={styles.connectDesc}>
            Your Google Business Profile is connected. Now choose which locations you want to track and sync reviews for.
          </p>
          <div className={styles.connectBenefits}>
            <div className={styles.benefitItem}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={styles.benefitCheck}><polyline points="20 6 9 17 4 12" /></svg>
              <span>Only selected locations will have reviews synced</span>
            </div>
            <div className={styles.benefitItem}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={styles.benefitCheck}><polyline points="20 6 9 17 4 12" /></svg>
              <span>You can add or change locations anytime from Settings</span>
            </div>
            <div className={styles.benefitItem}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={styles.benefitCheck}><polyline points="20 6 9 17 4 12" /></svg>
              <span>Select multiple locations at once to manage them together</span>
            </div>
          </div>
          <button
            type="button"
            className={styles.connectBtn}
            onClick={onOpenWizard}
          >
            <IconMapPin size={18} />
            Select Locations
          </button>
        </div>
      </div>
    );
  }

  // ── Case 3: Locations selected — show stats ───────────────────────────
  const { month, total, replied } = stats || { month: '', total: 0, replied: 0 };
  const pending   = total - replied;
  const replyRate = total > 0 ? Math.round((replied / total) * 100) : 0;

  // Label for the currently active location dropdown option
  const activeLocationName = activeLocationId === 'all'
    ? 'All Locations'
    : (selectedLocations.find(l => l.locationId === activeLocationId)?.locationName || activeLocationId);


  return (
    <div className={styles.statsWrap}>
      {/* ── Location filter dropdown (shown when at least one location is tracked) ── */}
      {selectedLocations.length > 0 && (
        <div className={styles.locationFilterRow}>
          <label className={styles.locationFilterLabel} htmlFor="location-filter">
            <IconMapPin size={14} />
            Viewing:
          </label>
          <div className={styles.locationFilterSelectWrap}>
            <select
              id="location-filter"
              className={styles.locationFilterSelect}
              value={activeLocationId}
              onChange={e => setActiveLocationId(e.target.value)}
            >
              <option value="all">All Locations ({selectedLocations.length})</option>
              {selectedLocations.map(loc => (
                <option key={loc.locationId} value={loc.locationId}>
                  {loc.locationName || loc.locationId}
                </option>
              ))}
            </select>
          </div>
          {statsLoading && <div className={styles.spinner} style={{ width: 18, height: 18, borderWidth: 2 }} />}
        </div>
      )}

      {/* Summary cards */}
      {statsLoading && !stats ? (
        <div className={styles.loadingWrap}><div className={styles.spinner} /></div>
      ) : (
      <div className={styles.statCards}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{total}</div>
          <div className={styles.statLabel}>Reviews This Month</div>
        </div>
        <div className={`${styles.statCard} ${styles.statCardGreen}`}>
          <div className={styles.statValue}>{replied}</div>
          <div className={styles.statLabel}>Replied</div>
        </div>
        <div className={`${styles.statCard} ${styles.statCardAmber}`}>
          <div className={styles.statValue}>{pending}</div>
          <div className={styles.statLabel}>Pending Reply</div>
        </div>
        <div className={`${styles.statCard} ${styles.statCardPurple}`}>
          <div className={styles.statValue}>{replyRate}%</div>
          <div className={styles.statLabel}>Reply Rate</div>
        </div>
      </div>
      )}

      {/* Table */}
      <div className={`${styles.tableWrap} ${statsLoading ? styles.tableWrapFading : ''}`}>
        <div className={styles.tableHeader}>
          <div className={styles.statsHeaderRow}>
            <div>
              <h2 className={styles.tableTitle}>
                Reviews — {month}
                {activeLocationId !== 'all' && (
                  <span className={styles.tableLocationBadge}>{activeLocationName}</span>
                )}
              </h2>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className={styles.tableBadge}>{total} total</span>
              <button
                type="button"
                className={styles.manageLocationsChip}
                onClick={onOpenWizard}
                title="Manage tracked locations"
              >
                <IconMapPin size={12} />
                {selectedLocations.length} location{selectedLocations.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>

        {rawReviews.length === 0 ? (
          <div className={styles.emptyState}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            <p>No reviews recorded for this month yet.</p>
            <span>Reviews will appear here once the scraper processes your selected locations.</span>
          </div>
        ) : (
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: 48 }}>#</th>
                  <th>Reviewer</th>
                  <th 
                    className={styles.thSortable}
                    onClick={() => handleSort('rating')}
                  >
                    <div className={styles.thSortableContent}>
                      Rating
                      <SortIcon active={sortField === 'rating'} direction={sortDirection} />
                    </div>
                  </th>
                  <th 
                    className={styles.thSortable}
                    onClick={() => handleSort('review')}
                  >
                    <div className={styles.thSortableContent}>
                      Review
                      <SortIcon active={sortField === 'review'} direction={sortDirection} />
                    </div>
                  </th>
                  <th>Reply</th>
                  <th
                    className={styles.thSortable}
                    style={{ width: 110 }}
                    onClick={() => handleSort('status')}
                  >
                    <div className={styles.thSortableContent}>
                      Status
                      <SortIcon active={sortField === 'status'} direction={sortDirection} />
                    </div>
                  </th>
                  <th
                    className={styles.thSortable}
                    style={{ width: 120 }}
                    onClick={() => handleSort('date')}
                  >
                    <div className={styles.thSortableContent}>
                      Date
                      <SortIcon active={sortField === 'date'} direction={sortDirection} />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedReviews.map((r, i) => {
                  const starColor = getStarColor(r.rating || 0);
                  const reviewerInitial = (r.reviewerName || 'A')[0].toUpperCase();
                  
                  return (
                    <tr key={r._id || i}>
                      <td className={styles.tdNum}>{i + 1}</td>
                      <td className={styles.tdName}>
                        <div className={styles.reviewerContainer}>
                          <div className={styles.reviewerAvatar}>
                            {reviewerInitial}
                          </div>
                          <span className={styles.reviewerName}>{r.reviewerName || 'Anonymous'}</span>
                        </div>
                      </td>
                      <td className={styles.tdRating}>
                        <span className={styles.stars}>
                          {[1,2,3,4,5].map(n => (
                            <svg key={n} width="14" height="14" viewBox="0 0 24 24"
                              fill={n <= (r.rating || 0) ? starColor : 'none'}
                              stroke={n <= (r.rating || 0) ? starColor : '#d1d5db'}
                              strokeWidth="2" aria-hidden="true">
                              <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"/>
                            </svg>
                          ))}
                        </span>
                      </td>
                      <td className={styles.tdReview}>
                        {r.reviewText ? (
                          <span className={styles.reviewText}>{r.reviewText}</span>
                        ) : (
                          <span className={styles.muted}>No review text</span>
                        )}
                      </td>
                      <td className={styles.tdReply}>
                        {r.replyText ? (
                          <div className={styles.replyContainer}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={styles.replyIcon}>
                              <path d="M9 17H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-5l-5 5v-5z"/>
                            </svg>
                            <span className={styles.replyText}>{r.replyText}</span>
                          </div>
                        ) : (
                          <span className={styles.muted}>—</span>
                        )}
                      </td>
                      <td>
                        <span className={r.replied ? styles.badgeReplied : styles.badgePending}>
                          <span className={styles.statusDot} />
                          {r.replied ? 'Replied' : 'Pending'}
                        </span>
                      </td>
                      <td className={styles.tdDate}>
                        {r.reviewDate ? new Date(r.reviewDate).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const BILLING_STATUS_META = {
  trialing:  { label: 'Free trial',   badgeClass: 'billingBadgeTrial' },
  active:    { label: 'Active',       badgeClass: 'billingBadgeActive' },
  past_due:  { label: 'Payment issue', badgeClass: 'billingBadgeAlert' },
  paused:    { label: 'Paused',       badgeClass: 'billingBadgePaused' },
  canceled:  { label: 'Canceled',     badgeClass: 'billingBadgeAlert' },
};

const PLAN_DISPLAY_NAMES = { starter: 'Starter', pro: 'Pro' };

// Static display-only price hints for the change-plan selectors below - kept
// in sync with the catalog by hand (same as PaymentPage.jsx's PLAN_INFO).
// The actual Price ID used to make the change is resolved server-side from
// PADDLE_PRICE_* env vars, this is only ever shown to the customer.
const PLAN_PRICE_HINTS = {
  starter: { monthly: 5, annual: 55 },
  pro:     { monthly: 10, annual: 110 },
};

// A plan can only be changed or canceled while it's actually billing - not
// once it's already fully canceled, and not again once a cancellation is
// already scheduled (the account page just shows the end date at that point).
const MANAGEABLE_STATUSES = ['active', 'trialing', 'past_due'];

const BillingPanel = ({ token }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [actionError, setActionError] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  const [planFormOpen, setPlanFormOpen] = useState(false);
  const [newPlan, setNewPlan] = useState('starter');
  const [newCycle, setNewCycle] = useState('monthly');

  const loadSubscription = useCallback(() => {
    if (!token) return undefined;
    setLoading(true);
    return safeApiFetch(getApiUrl('/api/payment/subscription'), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(d => {
        setSubscription(d.subscription);
        const [pk, cy] = ((d.subscription && d.subscription.planKey) || '').split('_');
        if (pk) setNewPlan(pk);
        if (cy) setNewCycle(cy);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { loadSubscription(); }, [loadSubscription]);

  const handleCancel = async () => {
    if (!window.confirm("Cancel your subscription? You'll keep access until the end of your current billing period.")) return;
    setCanceling(true);
    setActionError(null);
    setActionMessage(null);
    try {
      const data = await safeApiFetch(getApiUrl('/api/payment/subscription/cancel'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubscription(prev => ({ ...prev, ...data.subscription }));
      setActionMessage("Your subscription is scheduled to cancel at the end of the current billing period. You won't be charged again.");
    } catch (e) {
      setActionError(e.message || 'Failed to cancel subscription.');
    } finally {
      setCanceling(false);
    }
  };

  const handleChangePlan = async () => {
    setChangingPlan(true);
    setActionError(null);
    setActionMessage(null);
    try {
      const data = await safeApiFetch(getApiUrl('/api/payment/subscription/change-plan'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: newPlan, cycle: newCycle }),
      });
      setSubscription(prev => ({ ...prev, ...data.subscription }));
      setActionMessage(`Your plan has been switched to ${PLAN_DISPLAY_NAMES[newPlan] || newPlan} (${newCycle === 'annual' ? 'billed annually' : 'billed monthly'}).`);
      setPlanFormOpen(false);
    } catch (e) {
      setActionError(e.message || 'Failed to change plan.');
    } finally {
      setChangingPlan(false);
    }
  };

  if (loading) return <div className={styles.loadingWrap}><div className={styles.spinner} /></div>;
  if (error)   return <div className={styles.alertError}>{error}</div>;

  // ── Case 1: no subscription on file yet ───────────────────────────────
  if (!subscription) {
    return (
      <div className={styles.connectContainer}>
        <div className={styles.connectCard}>
          <div className={styles.connectIconWrap}>
            <IconBilling />
          </div>
          <h2 className={styles.connectTitle}>No active plan yet</h2>
          <p className={styles.connectDesc}>
            You haven't subscribed to a plan yet. Choose Starter or Pro to start automating your review replies — you can always connect your Google Business locations afterwards from this dashboard.
          </p>
          <Link to="/onboarding" className={styles.connectBtn}>
            Choose a plan
          </Link>
        </div>
      </div>
    );
  }

  // ── Case 2: subscription on file ──────────────────────────────────────
  const [planKey, cycle] = (subscription.planKey || subscription.packageDetails?.name || '').split('_');
  const planName = PLAN_DISPLAY_NAMES[subscription.packageDetails?.name || planKey] || subscription.packageDetails?.name || planKey || 'Plan';
  const billingCycle = subscription.packageDetails?.billingCycle || cycle;
  const statusMeta = BILLING_STATUS_META[subscription.status] || { label: subscription.status || 'Unknown', badgeClass: 'billingBadgePaused' };
  const periodEndLabel = subscription.status === 'trialing' ? 'Trial ends'
    : subscription.status === 'active' ? 'Renews on'
    : subscription.status === 'past_due' ? 'Payment retry by'
    : 'Access until';
  const card = subscription.cardDetails;
  const hasCard = card && card.cardLastFour;
  const canManage = MANAGEABLE_STATUSES.includes(subscription.status) && !subscription.scheduledCancelAt;
  const isSameAsCurrentPlan = newPlan === planKey && newCycle === billingCycle;

  return (
    <div className={styles.billingWrap}>
      <div className={styles.billingCard}>
        <div className={styles.billingHeader}>
          <div>
            <div className={styles.billingPlanName}>{planName} Plan</div>
            {billingCycle && (
              <div className={styles.billingCycleLabel}>
                {billingCycle === 'annual' ? 'Billed annually' : 'Billed monthly'}
              </div>
            )}
          </div>
          <span className={`${styles.billingBadge} ${styles[statusMeta.badgeClass]}`}>{statusMeta.label}</span>
        </div>

        <div className={styles.billingDetailsGrid}>
          {subscription.currentPeriodEnd && (
            <div className={styles.billingDetailItem}>
              <div className={styles.billingDetailLabel}>{periodEndLabel}</div>
              <div className={styles.billingDetailValue}>
                {new Date(subscription.currentPeriodEnd).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
            </div>
          )}
          {hasCard && (
            <div className={styles.billingDetailItem}>
              <div className={styles.billingDetailLabel}>Payment method</div>
              <div className={styles.billingDetailValue}>
                {(card.cardBrand || 'Card').toUpperCase()} •••• {card.cardLastFour}
                {card.cardExpiry ? ` (exp ${card.cardExpiry})` : ''}
              </div>
            </div>
          )}
        </div>

        {subscription.status === 'past_due' && (
          <div className={styles.alertError} style={{ marginTop: 20, marginBottom: 0 }}>
            Your last payment attempt failed. Paddle will automatically retry — update your card details if this continues.
          </div>
        )}

        {subscription.scheduledCancelAt && (
          <div className={styles.billingCancelNotice}>
            Your subscription is set to cancel on {new Date(subscription.scheduledCancelAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}. You'll keep full access until then.
          </div>
        )}

        {actionMessage && <div className={styles.savedMsg} style={{ marginTop: 16 }}>{actionMessage}</div>}
        {actionError && <div className={styles.alertError} style={{ marginTop: 16, marginBottom: 0 }}>{actionError}</div>}

        {canManage && (
          <>
            <div className={styles.divider} />

            {!planFormOpen ? (
              <div className={styles.billingActionsRow}>
                <button type="button" className={styles.manageLocationsBtn} onClick={() => setPlanFormOpen(true)}>
                  Change plan
                </button>
                <button type="button" className={styles.disconnectBtn} onClick={handleCancel} disabled={canceling}>
                  {canceling ? 'Canceling...' : 'Cancel subscription'}
                </button>
              </div>
            ) : (
              <div className={styles.billingPlanForm}>
                <div className={styles.billingPlanFormRow}>
                  <select className={styles.select} value={newPlan} onChange={e => setNewPlan(e.target.value)}>
                    {Object.keys(PLAN_DISPLAY_NAMES).map(p => (
                      <option key={p} value={p}>{PLAN_DISPLAY_NAMES[p]}</option>
                    ))}
                  </select>
                  <select className={styles.select} value={newCycle} onChange={e => setNewCycle(e.target.value)}>
                    <option value="monthly">{`Monthly ($${PLAN_PRICE_HINTS[newPlan]?.monthly}/mo)`}</option>
                    <option value="annual">{`Annual ($${PLAN_PRICE_HINTS[newPlan]?.annual}/yr)`}</option>
                  </select>
                </div>
                <div className={styles.billingActionsRow}>
                  <button
                    type="button"
                    className={styles.saveBtn}
                    onClick={handleChangePlan}
                    disabled={changingPlan || isSameAsCurrentPlan}
                  >
                    {changingPlan ? 'Updating...' : 'Confirm change'}
                  </button>
                  <button
                    type="button"
                    className={styles.manageLocationsBtn}
                    style={{ background: 'transparent', color: '#6b7280' }}
                    onClick={() => { setPlanFormOpen(false); setActionError(null); }}
                    disabled={changingPlan}
                  >
                    Cancel
                  </button>
                </div>
                {isSameAsCurrentPlan && (
                  <p className={styles.billingFootnote} style={{ marginTop: 8 }}>You're already on this plan.</p>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <p className={styles.billingFootnote}>
        Changes are prorated automatically by Paddle - switching plans adjusts your next charge, and canceling never charges you again after the current period ends.
      </p>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN — Customer Dashboard
// ═══════════════════════════════════════════════════════════════════════════
const PANELS = { settings: 'settings', stats: 'stats', billing: 'billing' };

const NAV = [
  { id: PANELS.stats,    label: 'My Stats', icon: <IconStats /> },
  { id: PANELS.billing,  label: 'Billing',  icon: <IconBilling /> },
  { id: PANELS.settings, label: 'Settings', icon: <IconSettings /> },
];

const PANEL_META = {
  [PANELS.stats]:    { title: 'My Stats',  subtitle: "This month's reviews and reply status" },
  [PANELS.billing]:  { title: 'Billing',   subtitle: 'Your plan, status, and payment method' },
  [PANELS.settings]: { title: 'Settings',  subtitle: 'Customise your reply tone and preferences' },
};

const CustomerDashboard = () => {
  const [activePanel, setActivePanel] = useState(PANELS.stats);
  const { t } = useLanguage();
  const [wizardOpen, setWizardOpen]   = useState(false);
  // selectedLocations: array of { locationId, locationName, address }
  // initialised from localStorage cache; wizard saves persist to DB
  const [selectedLocations, setSelectedLocations] = useState(() => {
    try { return JSON.parse(localStorage.getItem('gbp_selected_locations')) || []; }
    catch { return []; }
  });

  const navigate = useNavigate();

  const user = (() => {
    try { return JSON.parse(localStorage.getItem('user1')) || null; }
    catch { return null; }
  })();
  const token = localStorage.getItem('token1');

  useEffect(() => {
    if (!token) navigate('/?login=true', { replace: true });
  }, [token, navigate]);

  // Hydrate selectedLocations from backend on mount so we're always in sync
  useEffect(() => {
    if (!token) return;
    safeApiFetch(getApiUrl('/api/auth/google/locations'), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(d => {
        const locs = d.locations || [];
        if (locs.length > 0 && locs[0].selectedLocations?.length > 0) {
          const saved = locs[0].selectedLocations;
          setSelectedLocations(saved);
          localStorage.setItem('gbp_selected_locations', JSON.stringify(saved));
        }
      })
      .catch(() => {/* silent — we already have localStorage fallback */});
  }, [token]);

  const handleWizardSaved = useCallback((newSelection) => {
    setSelectedLocations(newSelection);
    localStorage.setItem('gbp_selected_locations', JSON.stringify(newSelection));
    setWizardOpen(false);
  }, []);

  const openWizard  = useCallback(() => setWizardOpen(true),  []);
  const closeWizard = useCallback(() => setWizardOpen(false), []);

  const meta = PANEL_META[activePanel];

  const renderContent = () => {
    switch (activePanel) {
      case PANELS.settings:
        return (
          <SettingsPanel
            token={token}
            selectedLocations={selectedLocations}
            onOpenWizard={openWizard}
          />
        );
      case PANELS.stats:
        return (
          <StatsPanel
            token={token}
            selectedLocations={selectedLocations}
            onOpenWizard={openWizard}
          />
        );
      case PANELS.billing:
        return <BillingPanel token={token} />;
      default:
        return null;
    }
  };

  return (
    <div className={styles.shell}>
      {/* ── Wizard Modal (rendered at root so both panels can trigger it) ── */}
      {wizardOpen && (
        <LocationWizardModal
          token={token}
          initialSelected={selectedLocations}
          onClose={closeWizard}
          onSaved={handleWizardSaved}
        />
      )}

      {/* ── Sidebar ─────────────────────────────────────────────── */}
      <aside className={styles.sidebar}>
        <div className={styles.sidebarTop}>
          {/* Brand */}
          <Link to="/" className={styles.brand}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#9219a6"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z"/>
              <path d="M7 11h10"/><path d="M7 15h6"/><path d="M7 7h8"/>
            </svg>
            ReplyShoot
          </Link>

          {/* User info */}
          {user && (
            <div className={styles.userCard}>
              <div className={styles.userAvatar}>
                {(user.name || user.email || 'U')[0].toUpperCase()}
              </div>
              <div className={styles.userInfo}>
                <div className={styles.userName}>{user.name || 'Account'}</div>
                <div className={styles.userEmail}>{user.email}</div>
              </div>
            </div>
          )}

          {/* Nav */}
          <nav className={styles.nav} aria-label="Customer navigation">
            <div className={styles.navLabel}>{t('menu', 'Menu')}</div>
            {NAV.map(item => (
              <button
                key={item.id}
                type="button"
                className={`${styles.navItem} ${activePanel === item.id ? styles.active : ''}`}
                onClick={() => setActivePanel(item.id)}
                aria-current={activePanel === item.id ? 'page' : undefined}
              >
                <span className={styles.navIcon}>{item.icon}</span>
                {item.label === 'Overview' ? t('overview', 'Overview') :
                 item.label === 'Brand Voice' ? t('brandVoice', 'Brand Voice') :
                 item.label === 'Locations' ? t('connectedLocations', 'Locations') :
                 item.label === 'Billing' ? t('billing', 'Billing') : item.label}
              </button>
            ))}
          </nav>
        </div>

        <div className={styles.sidebarFooter}>
          <Link to="/" className={styles.backBtn}>
            <IconBack /> {t('backToSite', 'Back to site')}
          </Link>
        </div>
      </aside>

      {/* ── Main ────────────────────────────────────────────────── */}
      <div className={styles.main}>
        <header className={styles.topbar} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 className={styles.pageTitle}>{meta.title}</h1>
            <p className={styles.pageSubtitle}>{meta.subtitle}</p>
          </div>
          <LanguageToggle />
        </header>

        <div className={styles.content}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;
